import { PerspectiveCamera, Scene, Vector3 } from 'three';
import {
  COMBAT,
  CollisionWorld,
  FLAG,
  MODE,
  NO_INPUT,
  RIDER_COLORS,
  RIDE_TUNING,
  RailNet,
  RiderSim,
  WORLD,
  buildCourse,
  cleanName,
  round,
} from '@thrasher/shared';
import type {
  AttackKind,
  Course,
  HitEvent,
  Ride,
  RiderState,
  RosterEntry,
  SimEvent,
  SimInput,
  Snapshot,
  SpawnPoint,
} from '@thrasher/shared';
import { ArcadeAudio } from './Audio.js';
import { CameraRig } from './CameraRig.js';
import { CourseView } from './CourseView.js';
import { Effects } from './Effects.js';
import { Environment } from './Environment.js';
import { Input } from './Input.js';
import type { InputFrame } from './Input.js';
import { Net } from './Net.js';
import { RemoteRider, hashSeed } from './RemoteRider.js';
import { Renderer } from './Renderer.js';
import type { Quality } from './Renderer.js';
import { RiderAvatar } from './RiderAvatar.js';
import type { HudStore } from './hudStore.js';

export interface GameOptions {
  container: HTMLElement;
  store: HudStore;
  quality: Quality;
  /** Offline sandbox (no server), used for local previews and screenshots. */
  offline?: boolean;
  /** Fixed camera "x,y,z,tx,ty,tz" for screenshots. */
  shot?: string | null;
  /** Stop the render loop after this many frames (headless screenshots). */
  maxFrames?: number | null;
}

export interface MapMarker {
  x: number;
  z: number;
  heading: number;
  color: string;
  self: boolean;
}

const UP = new Vector3(0, 1, 0);

/**
 * Owns the whole client session: renderer, park, local rider simulation, networking,
 * remote interpolation, combat feedback, audio and HUD updates.
 */
export class Game {
  public readonly course: Course;
  private readonly scene = new Scene();
  private readonly camera = new PerspectiveCamera(68, 1, 0.1, 5000);
  private readonly renderer: Renderer;
  private readonly environment: Environment;
  private readonly courseView: CourseView;
  private readonly world: CollisionWorld;
  private readonly rails: RailNet;
  private readonly cameraRig: CameraRig;
  private readonly effects: Effects;
  private readonly input = new Input();
  private readonly audio = new ArcadeAudio();
  private readonly remotes = new Map<string, RemoteRider>();
  private readonly store: HudStore;
  private net: Net | null = null;
  private sim: RiderSim | null = null;
  private avatar: RiderAvatar | null = null;
  private selfId: string | null = null;
  private colorIndex = 0;
  private ride: Ride = 'skateboard';
  private frameHandle = 0;
  private lastFrame = 0;
  private time = 0;
  private sendTimer = 0;
  private hudTimer = 0;
  private readonly lastAttack: Record<AttackKind, number> = { punch: -1e9, kick: -1e9 };
  private attackUntil = 0;
  private attackKind: AttackKind | null = null;
  private hurt = 0;
  private koUntil: number | null = null;
  private protectedUntil = 0;
  private trickId = 0;
  private perfSamples: number[] = [];
  private qualityLocked = false;
  private readonly offline: boolean;
  private readonly shot: number[] | null;
  private disposed = false;
  private frames = 0;
  private readonly maxFrames: number | null;

  public constructor(options: GameOptions) {
    this.store = options.store;
    this.offline = options.offline ?? false;
    this.shot = options.shot ? options.shot.split(',').map(Number).filter(Number.isFinite) : null;
    this.maxFrames = options.maxFrames ?? null;
    this.course = buildCourse();
    this.world = new CollisionWorld(this.course.collision);
    this.rails = new RailNet(this.course.rails);
    this.renderer = new Renderer(options.container, this.scene, this.camera, options.quality);
    const profile = this.renderer.quality;
    this.environment = new Environment(this.scene, this.renderer.renderer, {
      shadows: profile.shadows,
      shadowMapSize: profile.shadowMapSize,
    });
    this.courseView = new CourseView(this.course);
    this.scene.add(this.courseView.group);
    this.cameraRig = new CameraRig(this.camera, this.world);
    this.effects = new Effects(this.scene);
    this.store.set({ quality: options.quality, muted: this.audio.isMuted });
    window.addEventListener('resize', this.onResize);
    this.lastFrame = performance.now();
    this.frameHandle = requestAnimationFrame(this.frame);
  }

  private readonly onResize = (): void => this.renderer.resize();

  // ------------------------------------------------------------------ session

  /** Joins the one shared session (or starts the offline sandbox). */
  public async join(name: string, ride: Ride, token: string | null): Promise<void> {
    this.audio.start();
    this.ride = ride;
    this.store.set({ phase: 'joining', error: null, ride, name });
    if (this.offline) {
      this.selfId = 'local';
      this.spawnLocal(this.course.spawns[0]!, ride, 0);
      this.store.set({
        phase: 'playing',
        selfId: 'local',
        connected: true,
        name: cleanName(name) || 'Rider',
      });
      return;
    }
    if (!this.net) this.net = this.createNet();
    const result = await this.net.hello(name, ride, token);
    if (this.disposed) return;
    if (!result.ok) {
      const messages: Record<string, string> = {
        full: 'The park is full right now. Try again in a minute.',
        version: 'A new version of Thrasher is live. Refresh the page to update.',
        rate: 'Too many attempts. Wait a moment and try again.',
        joined: 'You are already riding in another tab.',
        invalid: 'Could not reach the park. Check your connection and try again.',
      };
      this.store.set({ phase: 'error', error: messages[result.error] ?? messages.invalid! });
      return;
    }
    const welcome = result.welcome;
    this.selfId = welcome.id;
    this.colorIndex = welcome.color;
    this.spawnLocal(welcome.spawn, welcome.ride, welcome.color);
    this.protectedUntil = this.time + COMBAT.spawnProtectionMs / 1000;
    this.applyRoster(welcome.riders);
    this.store.set({
      phase: 'playing',
      selfId: welcome.id,
      name: welcome.name,
      ride: welcome.ride,
      coins: welcome.coins,
      riders: welcome.riders,
      connected: true,
    });
    this.store.pushFeed('Dropped in. Space to ollie, P punch, K kick, H for help.', 'info');
  }

  private createNet(): Net {
    const net = new Net({
      snapshot: (snapshot) => this.onSnapshot(snapshot),
      roster: (riders) => {
        this.applyRoster(riders);
        this.store.set({ riders });
      },
      hit: (hit) => this.onHit(hit),
      respawn: (event) => {
        if (event.id === this.selfId) {
          this.sim?.teleport(event.spawn.p, event.spawn.yaw);
          this.sim?.releaseKnockout();
          this.cameraRig.snap();
          this.koUntil = null;
          this.protectedUntil = this.time + COMBAT.spawnProtectionMs / 1000;
          this.store.set({ hp: event.hp, koSeconds: null });
        } else {
          this.remotes.get(event.id)?.teleport();
        }
      },
      feed: (event) => {
        if (event.kind === 'join') this.store.pushFeed(`${event.name} dropped in`, 'join');
        else if (event.kind === 'leave')
          this.store.pushFeed(`${event.name} left the park`, 'leave');
        else
          this.store.pushFeed(
            `${event.by} ${event.attack === 'kick' ? 'booted' : 'decked'} ${event.victim}`,
            'ko',
          );
      },
      award: (event) => {
        this.audio.coins();
        this.store.set({ coins: event.total, award: { id: Date.now(), coins: event.coins } });
      },
      connection: (connected, reconnected) => {
        this.store.set({ connected });
        // The server forgets riders on disconnect; rejoin with the same choices.
        if (connected && reconnected && this.selfId && this.store.get().phase === 'playing') {
          void this.rejoin();
        }
      },
    });
    return net;
  }

  private async rejoin(): Promise<void> {
    if (!this.net) return;
    const state = this.store.get();
    const result = await this.net.hello(state.name, this.ride, this.tokenForRejoin);
    if (result.ok) {
      const welcome = result.welcome;
      this.selfId = welcome.id;
      this.colorIndex = welcome.color;
      this.spawnLocal(welcome.spawn, this.ride, welcome.color);
      this.koUntil = null;
      this.protectedUntil = this.time + COMBAT.spawnProtectionMs / 1000;
      this.applyRoster(welcome.riders);
      this.store.set({
        selfId: welcome.id,
        riders: welcome.riders,
        koSeconds: null,
        hp: COMBAT.maxHp,
      });
      this.store.pushFeed('Reconnected to the park.', 'info');
    }
  }

  private tokenForRejoin: string | null = null;

  public setToken(token: string | null): void {
    this.tokenForRejoin = token;
  }

  private spawnLocal(spawn: SpawnPoint, ride: Ride, color: number): void {
    this.ride = ride;
    if (!this.sim) this.sim = new RiderSim(this.world, this.rails, ride);
    this.sim.setRide(ride);
    this.sim.teleport(spawn.p, spawn.yaw);
    this.rebuildLocalAvatar(color);
    this.cameraRig.snap();
  }

  private rebuildLocalAvatar(color: number): void {
    this.avatar?.dispose();
    this.avatar = new RiderAvatar({
      ride: this.ride,
      color: RIDER_COLORS[color % RIDER_COLORS.length]!,
      seed: hashSeed(this.selfId ?? 'local'),
    });
    this.scene.add(this.avatar.root);
  }

  public setQuality(quality: Quality): void {
    this.qualityLocked = true;
    const profile = this.renderer.setQuality(quality);
    this.environment.setShadows(profile.shadows, profile.shadowMapSize);
    this.store.set({ quality });
  }

  public toggleMute(): void {
    this.store.set({ muted: this.audio.toggleMute() });
  }

  public setMenu(open: boolean): void {
    this.store.set({ menu: open });
  }

  /**
   * Leaves the shared session and returns to the game lobby (entry screen). Closing the
   * socket makes the server drop the rider and flush any hub rewards; Drop in rejoins
   * with a fresh connection.
   */
  public exitToLobby(): void {
    this.net?.close();
    this.net = null;
    for (const [id, remote] of this.remotes) {
      remote.dispose();
      this.effects.removeShadow(id);
    }
    this.remotes.clear();
    this.effects.removeShadow('self');
    this.avatar?.dispose();
    this.avatar = null;
    this.sim = null;
    this.selfId = null;
    this.koUntil = null;
    this.attackKind = null;
    this.hurt = 0;
    this.protectedUntil = 0;
    this.audio.drive(0, false, false, false);
    this.cameraRig.snap();
    this.store.set({
      phase: 'preview',
      menu: false,
      help: false,
      scoreboard: false,
      selfId: null,
      riders: [],
      feed: [],
      trick: null,
      balance: null,
      charge: 0,
      koSeconds: null,
      hp: COMBAT.maxHp,
      connected: false,
    });
  }

  public setHelp(open: boolean): void {
    this.store.set({ help: open });
  }

  public requestReset(): void {
    if (this.koUntil !== null) return;
    if (this.offline || !this.net) {
      const spawn = this.nearestSpawn();
      this.sim?.teleport(spawn.p, spawn.yaw);
      this.cameraRig.snap();
      return;
    }
    this.net.reset();
  }

  private nearestSpawn(): SpawnPoint {
    const pos = this.sim?.pos ?? new Vector3();
    let best = this.course.spawns[0]!;
    let bestDistance = Infinity;
    for (const spawn of this.course.spawns) {
      const d = (spawn.p[0] - pos.x) ** 2 + (spawn.p[2] - pos.z) ** 2;
      if (d < bestDistance) {
        bestDistance = d;
        best = spawn;
      }
    }
    return best;
  }

  private applyRoster(riders: RosterEntry[]): void {
    const seen = new Set<string>();
    for (const entry of riders) {
      if (entry.id === this.selfId) {
        if (entry.color !== this.colorIndex) {
          this.colorIndex = entry.color;
          this.rebuildLocalAvatar(entry.color);
        }
        continue;
      }
      seen.add(entry.id);
      const existing = this.remotes.get(entry.id);
      if (existing) existing.updateEntry(entry);
      else this.remotes.set(entry.id, new RemoteRider(this.scene, entry));
    }
    for (const [id, remote] of this.remotes) {
      if (!seen.has(id)) {
        remote.dispose();
        this.effects.removeShadow(id);
        this.remotes.delete(id);
      }
    }
  }

  private onSnapshot(snapshot: Snapshot): void {
    for (const rider of snapshot.riders) {
      if (rider.id === this.selfId) {
        this.store.set({ hp: rider.hp });
        if (!rider.prot) this.protectedUntil = Math.min(this.protectedUntil, this.time);
        continue;
      }
      this.remotes.get(rider.id)?.push(rider, snapshot.t);
    }
  }

  private onHit(hit: HitEvent): void {
    const heavy = hit.kind === 'kick' || hit.ko;
    const victimPosition = new Vector3();
    if (hit.to === this.selfId && this.sim) {
      const attack = COMBAT.attacks[hit.kind];
      this.sim.applyHit(hit.kb, attack.balanceShock, hit.ko);
      this.cameraRig.kick(heavy ? 1 : 0.6);
      this.hurt = Math.min(1, this.hurt + (heavy ? 0.9 : 0.6));
      victimPosition.copy(this.sim.pos);
      if (hit.ko) {
        this.koUntil = this.time + COMBAT.koRespawnMs / 1000;
        this.audio.knockout();
      }
      this.store.set({ hp: hit.hp });
    } else {
      const remote = this.remotes.get(hit.to);
      if (remote) {
        remote.hitFlash = 1;
        victimPosition.copy(remote.position);
      }
    }
    if (hit.from === this.selfId) {
      this.cameraRig.kick(heavy ? 0.4 : 0.25);
      this.store.set({ hitMarker: Date.now() });
    }
    this.audio.impact(heavy);
    if (victimPosition.lengthSq() > 0)
      this.effects.hit(victimPosition.add(new Vector3(0, 1.2, 0)), heavy);
  }

  // ------------------------------------------------------------------ frame loop

  private readonly frame = (now: number): void => {
    if (this.disposed) return;
    this.frames += 1;
    if (this.maxFrames === null || this.frames < this.maxFrames)
      this.frameHandle = requestAnimationFrame(this.frame);
    const dt = Math.min(0.1, Math.max(0, (now - this.lastFrame) / 1000));
    this.lastFrame = now;
    this.time += dt;
    const frameInput = this.input.poll();
    this.handleUi(frameInput);

    if (this.sim && this.avatar) this.updateLocal(dt, frameInput);
    else if (!this.shot) this.cameraRig.orbit(this.time, new Vector3(0, 0, -10), 78, 34);

    const renderTime = (this.net?.serverNow() ?? 0) - WORLD.interpolationDelayMs;
    const groundAt = this.groundAt;
    for (const [id, remote] of this.remotes) {
      remote.update(dt, renderTime, groundAt);
      this.updateShadow(id, remote.position, true);
      if (
        remote.mode === MODE.grind &&
        remote.position.distanceToSquared(this.camera.position) < 3600
      ) {
        this.effects.sparks(remote.position, new Vector3().copy(remote.velocity).normalize(), 0.7);
      }
    }
    if (this.shot && this.shot.length >= 6) {
      this.camera.position.set(this.shot[0]!, this.shot[1]!, this.shot[2]!);
      this.camera.lookAt(this.shot[3]!, this.shot[4]!, this.shot[5]!);
    }
    this.environment.follow(this.sim?.pos ?? new Vector3(0, 0, 0), this.camera.position, this.time);
    this.effects.update(dt);
    this.courseView.update(this.time);
    this.hurt = Math.max(0, this.hurt - dt * 1.5);
    this.renderer.setHurt(this.hurt);
    this.renderer.render();
    this.trackPerformance(dt);
  };

  private readonly groundAt = (x: number, y: number, z: number): number => {
    const hit = this.world.raycast([x, y, z], [0, -1, 0], 30);
    return hit ? hit.point[1] : -Infinity;
  };

  private handleUi(frame: InputFrame): void {
    const state = this.store.get();
    if (frame.menu && state.phase === 'playing') {
      // Esc closes help first, then toggles the pause menu.
      if (state.help) this.store.set({ help: false });
      else this.store.set({ menu: !state.menu });
    }
    if (frame.help) this.store.set({ help: !state.help });
    if (frame.mute) this.toggleMute();
    if (state.scoreboard !== frame.scoreboard) this.store.set({ scoreboard: frame.scoreboard });
    if (frame.reset && state.phase === 'playing' && !state.menu) this.requestReset();
  }

  private updateLocal(dt: number, frame: InputFrame): void {
    const sim = this.sim!;
    const avatar = this.avatar!;
    const state = this.store.get();
    const locked = state.menu || state.phase !== 'playing';
    const input: SimInput = locked
      ? NO_INPUT
      : {
          steer: frame.steer,
          push: frame.push,
          brake: frame.brake,
          jump: frame.jump,
          flip: frame.flip,
          grab: frame.grab,
        };
    const events = sim.update(dt, input);
    for (const event of events) this.onSimEvent(event);

    if (!locked && this.koUntil === null && sim.mode !== 'bail') {
      if (frame.punch) this.tryAttack('punch');
      if (frame.kick) this.tryAttack('kick');
    }
    if (this.koUntil !== null && this.time > this.koUntil + 4) {
      // Respawn never arrived (connection trouble): get back up locally.
      this.koUntil = null;
      sim.releaseKnockout();
    }

    const attacking = this.attackKind !== null && this.time < this.attackUntil;
    if (!attacking) this.attackKind = null;
    const trick = sim.trickCode;
    const flags =
      (sim.charge > 0.05 || sim.pumping ? FLAG.crouch : 0) |
      (sim.grabVariant >= 0 ? FLAG.grab : 0) |
      (sim.flip ? FLAG.flip : 0) |
      (attacking && this.attackKind === 'punch' ? FLAG.punch : 0) |
      (attacking && this.attackKind === 'kick' ? FLAG.kick : 0) |
      (sim.pushing ? FLAG.push : 0) |
      (sim.vert ? FLAG.vert : 0) |
      (sim.braking ? FLAG.brake : 0);

    avatar.root.position.copy(sim.pos);
    avatar.root.quaternion.copy(sim.quaternion);
    avatar.update(dt, {
      mode: sim.wireMode,
      flags,
      trick: trick.k,
      phase: trick.a,
      speed: sim.speed,
      steer: input.steer,
      attack: null,
      hit: this.hurt,
      protected: this.time < this.protectedUntil,
      groundAt: this.groundAt,
    });

    if (sim.mode === 'grind') {
      const contact = new Vector3().copy(sim.pos).addScaledVector(UP, -sim.tuning.grindOffset);
      this.effects.sparks(contact, new Vector3().copy(sim.vel).normalize(), 1.2);
    }
    this.updateShadow('self', sim.pos, sim.mode === 'air' || sim.mode === 'bail');
    this.audio.drive(sim.speed, sim.mode === 'ground', sim.mode === 'grind', sim.mode === 'air');

    const wall = sim.vert
      ? sim.vert.normal
      : sim.mode === 'ground' && sim.up.y < 0.5
        ? new Vector3(sim.up.x, 0, sim.up.z).normalize()
        : null;
    if (!this.shot) {
      this.cameraRig.update(dt, {
        pos: sim.pos,
        vel: sim.vel,
        fwd: sim.fwd,
        up: sim.up,
        mode: sim.mode,
        wall,
      });
    }

    this.sendTimer += dt;
    if (this.sendTimer >= 1 / WORLD.clientStateHz) {
      this.sendTimer = 0;
      this.net?.sendState(this.wireState(sim, flags, trick.k, trick.a));
    }
    this.hudTimer += dt;
    if (this.hudTimer >= 0.05) {
      this.hudTimer = 0;
      const nowMs = this.time * 1000;
      this.store.set({
        mode: sim.wireMode,
        balance: sim.mode === 'grind' ? round(sim.balance, 2) : null,
        charge: round(sim.charge, 2),
        speed: Math.round(sim.speed * 3.6),
        punchReady: round(
          Math.min(1, (nowMs - this.lastAttack.punch) / COMBAT.attacks.punch.cooldownMs),
          2,
        ),
        kickReady: round(
          Math.min(1, (nowMs - this.lastAttack.kick) / COMBAT.attacks.kick.cooldownMs),
          2,
        ),
        koSeconds: this.koUntil !== null ? Math.max(0, Math.ceil(this.koUntil - this.time)) : null,
        protectedSeconds: Math.max(0, Math.ceil(this.protectedUntil - this.time)),
        zone: this.zoneName(sim.pos),
      });
    }
  }

  private wireState(sim: RiderSim, flags: number, k: number, a: number): RiderState {
    const q = sim.quaternion;
    return {
      p: [round(sim.pos.x), round(sim.pos.y), round(sim.pos.z)],
      q: [round(q.x, 4), round(q.y, 4), round(q.z, 4), round(q.w, 4)],
      v: [round(sim.vel.x, 2), round(sim.vel.y, 2), round(sim.vel.z, 2)],
      m: sim.wireMode,
      f: flags,
      k,
      a: round(a, 3),
    };
  }

  private tryAttack(kind: AttackKind): void {
    const nowMs = this.time * 1000;
    const attack = COMBAT.attacks[kind];
    if (nowMs - this.lastAttack[kind] < attack.cooldownMs) return;
    this.lastAttack[kind] = nowMs;
    this.attackKind = kind;
    this.attackUntil = this.time + attack.animMs / 1000;
    this.protectedUntil = Math.min(this.protectedUntil, this.time);
    this.avatar?.attack(kind);
    this.audio.whoosh();
    this.net?.attack(kind);
  }

  private onSimEvent(event: SimEvent): void {
    const sim = this.sim!;
    switch (event.type) {
      case 'ollie':
        this.audio.ollie();
        break;
      case 'land': {
        this.audio.land(event.impact);
        if (event.impact > 4 || event.airTime > 0.5)
          this.effects.dust(sim.pos, Math.min(1, event.impact / 10));
        if (event.impact > 9) this.cameraRig.kick(0.25);
        const names =
          event.tricks.length > 0 ? event.tricks : event.airTime > 1.3 ? ['Big Air'] : [];
        if (names.length > 0) {
          this.trickId += 1;
          this.store.set({ trick: { id: this.trickId, text: names.join(' + ') } });
        }
        break;
      }
      case 'grindStart':
        this.audio.grindStart();
        break;
      case 'bail':
        this.audio.bail();
        this.effects.dust(sim.pos, 1);
        this.cameraRig.kick(0.5);
        break;
      case 'wallHit':
        if (event.speed > 6) this.cameraRig.kick(Math.min(0.5, event.speed * 0.03));
        break;
      default:
        break;
    }
  }

  private updateShadow(id: string, position: Vector3, visible: boolean): void {
    if (!visible) {
      this.effects.shadow(id, false, null, null, 0);
      return;
    }
    const hit = this.world.raycast([position.x, position.y + 0.4, position.z], [0, -1, 0], 30);
    if (!hit) {
      this.effects.shadow(id, false, null, null, 0);
      return;
    }
    const height = position.y - hit.point[1];
    this.effects.shadow(id, true, new Vector3(...hit.point), new Vector3(...hit.normal), height);
  }

  private zoneName(position: Vector3): string {
    let best = '';
    let bestDistance = Infinity;
    for (const zone of this.course.zones) {
      const d = (zone.x - position.x) ** 2 + (zone.z - position.z) ** 2;
      if (d < bestDistance) {
        bestDistance = d;
        best = zone.name;
      }
    }
    return best;
  }

  private trackPerformance(dt: number): void {
    if (this.qualityLocked || !this.sim) return;
    this.perfSamples.push(dt);
    if (this.perfSamples.length < 240) return;
    const sorted = [...this.perfSamples].sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)]!;
    this.perfSamples = [];
    const quality = this.store.get().quality;
    if (median > 1 / 38 && quality !== 'low') {
      const next: Quality = quality === 'high' ? 'medium' : 'low';
      const profile = this.renderer.setQuality(next);
      this.environment.setShadows(profile.shadows, profile.shadowMapSize);
      this.store.set({ quality: next });
      this.store.pushFeed(`Graphics set to ${next} for a smoother ride (Esc to change).`, 'info');
    } else {
      this.qualityLocked = true;
    }
  }

  /** Minimap markers for the HUD. */
  public markers(): MapMarker[] {
    const markers: MapMarker[] = [];
    for (const remote of this.remotes.values()) {
      const forward = new Vector3(0, 0, 1).applyQuaternion(remote.quaternion);
      markers.push({
        x: remote.position.x,
        z: remote.position.z,
        heading: Math.atan2(forward.x, forward.z),
        color: RIDER_COLORS[remote.color % RIDER_COLORS.length]!,
        self: false,
      });
    }
    if (this.sim) {
      const forward = new Vector3().copy(this.sim.fwd);
      const cameraForward = new Vector3(0, 0, -1).applyQuaternion(this.camera.quaternion);
      markers.push({
        x: this.sim.pos.x,
        z: this.sim.pos.z,
        heading: Math.atan2(forward.x || cameraForward.x, forward.z || cameraForward.z),
        color: RIDER_COLORS[this.colorIndex % RIDER_COLORS.length]!,
        self: true,
      });
    }
    return markers;
  }

  /** Camera yaw for rotating the minimap with the view. */
  public cameraYaw(): number {
    const forward = new Vector3(0, 0, -1).applyQuaternion(this.camera.quaternion);
    return Math.atan2(forward.x, forward.z);
  }

  public rideLabel(): string {
    return RIDE_TUNING[this.ride].label;
  }

  public dispose(): void {
    this.disposed = true;
    cancelAnimationFrame(this.frameHandle);
    window.removeEventListener('resize', this.onResize);
    this.net?.close();
    this.input.dispose();
    this.audio.dispose();
    for (const remote of this.remotes.values()) remote.dispose();
    this.avatar?.dispose();
    this.effects.dispose();
    this.environment.dispose();
    this.renderer.dispose();
  }
}
